import React, { FC } from 'react'
import pluralize from 'pluralize'
import { Button, Grid } from '@mui/material'
import { DialogNames } from 'src/enums'
import { buildBulkResultMessages } from 'src/helpers'
import { useMakeSensorsReadyToUseBulk } from 'src/hooks/api'
import { useAddPopupMessage, useDialog } from 'src/hooks/ui'
import { BulkConfirmationModal } from './bulkConfirmationModal'

interface SelectedSensor {
  uniqueId: string
  imei: string | number | null
}

interface MakeSensorsReadyToUseModalProps {
  selectedSensors: SelectedSensor[]
  onSuccess?: () => void
}

export const MakeSensorsReadyToUseModal: FC<MakeSensorsReadyToUseModalProps> = ({ selectedSensors, onSuccess }) => {
  const { openDialog, closeDialog } = useDialog(DialogNames.MakeSensorsReadyToUse)
  const addPopupMessage = useAddPopupMessage()
  const count = selectedSensors.length

  const makeSensorsReadyToUseBulk = useMakeSensorsReadyToUseBulk({
    options: {
      onSuccess: (results) => {
        const failed = results.filter((item) => item.status !== 'ok')
        addPopupMessage(
          buildBulkResultMessages(
            results,
            (n) => `Successfully made ${n} sensor${n !== 1 ? 's' : ''} available.`,
            (uniqueId, message) => `Failed to update sensor ${uniqueId}: ${message}`,
          ),
        )
        if (failed.length === 0) closeDialog()
        onSuccess?.()
      },
      onError: (error) => {
        addPopupMessage({ text: error.message, type: 'error' })
      },
    },
  })

  return (
    <Grid>
      <Button variant="outlined" onClick={openDialog} size="large">
        Make ready to use
      </Button>
      <BulkConfirmationModal
        name={DialogNames.MakeSensorsReadyToUse}
        title="Make sensors 'Available'"
        confirmationText={`Are you sure to make ${pluralize('sensor', count, true)} "Available"?`}
        selectedSensors={selectedSensors}
        onConfirm={() => makeSensorsReadyToUseBulk.mutate({ uniqueIds: selectedSensors.map((s) => s.uniqueId) })}
        onClose={() => closeDialog()}
        isLoading={makeSensorsReadyToUseBulk.isLoading}
      />
    </Grid>
  )
}
